<template>
  <transition
    :name="TRANSITION_NAME.SECTION"
    mode="out-in"
    @enter="onEnter"
    @after-enter="onAfterEnter"
    @enter-cancelled="onAfterEnter"
    @leave="onLeave"
  >
    <div
      v-if="showSkeleton"
      key="skeleton"
      v-bind="$attrs"
      aria-busy="true"
    >
      <SkeletonLoader
        :height="reserveHeight"
        data-testid="skeleton"
      />
    </div>
    <div
      v-else-if="showContent"
      key="content"
      v-bind="$attrs"
    >
      <slot />
    </div>
  </transition>
</template>

<script lang="ts">

/**
 * Fades a section in once it has something to show, growing the space it takes from whatever it
 * replaces to its own height, so that the rest of the page is pushed down gradually rather than
 * all at once.
 *
 * With reserveHeight it also holds the section's place with a skeleton of that height while
 * waiting, which is what sections that are known to be there want. Without it nothing is
 * rendered until the content arrives, which is what sections that may legitimately stay empty
 * want, so that no space is reserved for content that may never come.
 */

import { defineComponent, computed } from 'vue';
import { TRANSITION_NAME } from '~~/constants/transitions';
import { useIsMounted } from '~/composables/useIsMounted';

export default defineComponent({
  name: 'SectionTransition',
  inheritAttrs: false,
  props: {
    /** Whether there is anything to render, usually known only once the data is loaded. */
    hasContent: {
      type: Boolean,
      required: true,
    },
    /**
     * Height of the skeleton that holds the section's place until it has content. Without it
     * the section stays out of the layout entirely while waiting.
     */
    reserveHeight: {
      type: [String, Number],
      default: '',
    },
    /** Withholds the content until mounted, so that it never renders on the server. */
    clientOnly: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {

    const { isMounted } = useIsMounted();

    const showContent = computed(() => props.hasContent && (isMounted.value || !props.clientOnly));

    const showSkeleton = computed(() => Boolean(props.reserveHeight) && !showContent.value);

    /** What the outgoing section occupied, so that the incoming one can grow from there. */
    let previousHeight = 0;

    const onLeave = (el: Element) => {

      previousHeight = (el as HTMLElement).offsetHeight;

    };

    const onEnter = (el: Element) => {

      const section = el as HTMLElement;
      const contentHeight = section.scrollHeight;

      section.style.height = `${previousHeight}px`;

      // Reading the layout back applies that starting height. Without it the browser only ever
      // sees the final one and jumps straight to it.
      void section.offsetHeight;

      section.style.height = `${contentHeight}px`;

    };

    const onAfterEnter = (el: Element) => {

      // Back to auto, so that the section keeps following its content afterwards.
      (el as HTMLElement).style.height = '';
      previousHeight = 0;

    };

    return {
      showContent,
      showSkeleton,
      onEnter,
      onAfterEnter,
      onLeave,
      TRANSITION_NAME,
    };

  },
});

</script>
